import {
  defaultTestDynamicConfig,
  ITestDynamicConfig,
  testDynamicConfigSchema,
} from '@common/interfaces/config/dynamicConfigs/ITestDynamicConfig';
import {
  boolValue,
  numberValue,
  stringValue,
} from '@common/utils/dynamicConfig/firebase/FirebaseValueSchemas';
import { z } from 'zod';
import { IFeatureFlags } from '@common/interfaces/config/IFeatureFlags';
import {
  IViewingsImportConfig,
  viewingsImportConfigDefaults,
  viewingsImportConfigSchema,
} from '@common/utils/dynamicConfig/parts/viewingsConfig';
import {
  contactFormConfigDefaults,
  contactFormConfigSchema,
  IContactFormConfig,
} from '@common/utils/dynamicConfig/parts/contactFormConfig';

/**
 * Provides the dynamic configuration for the application.
 *
 * This is generally loaded from Firebase Remote Config.
 */
export type IDynamicConfig = {
  test: ITestDynamicConfig;
  missingTest: ITestDynamicConfig;
  bareString: string;
  bareNumber: number;
  bareBoolean: boolean;
  viewingsImport: IViewingsImportConfig;
  contactForm: IContactFormConfig;
  /**
   * The oldest Passport version that is issued Okta tokens (OKTA-15). Older builds, and builds that
   * don't report a version, keep getting Bruntwood tokens. Set to the release containing BCD-1796.
   */
  oktaIssuancePassportMinVersion: string;
};

/**
 * The default dynamic configuration values.
 *
 * @remarks there are test variables here - these are used in integration tests. I considered having it separately, but
 *  it made the type signatures a lot harder to read for no real benefit.
 */
export const defaultDynamicConfigs: IDynamicConfig = {
  // Test configs below here. Do not modify these unless you know what you're doing.
  test: defaultTestDynamicConfig,
  missingTest: defaultTestDynamicConfig,
  viewingsImport: viewingsImportConfigDefaults,
  bareString: 'default string valu{}!!',
  bareNumber: 42,
  bareBoolean: false,
  contactForm: contactFormConfigDefaults,
  // Unreachable on purpose: no Passport build qualifies until the floor is set in Remote Config.
  oktaIssuancePassportMinVersion: '999.0.0',
};

/**
 * A lookup for zod schemas for each dynamic config section.
 * We don't generate one big schema for everything, because if one part fails we don't want to fail
 * loading the entire config.
 * Instead, this is an object that contains a mapping from config key to zod schema.
 */
export const dynamicConfigSchemas = {
  test: testDynamicConfigSchema,
  missingTest: testDynamicConfigSchema,
  bareString: stringValue.pipe(z.string().default(defaultDynamicConfigs.bareString)),
  bareBoolean: boolValue.pipe(z.boolean().default(defaultDynamicConfigs.bareBoolean)),
  bareNumber: numberValue.pipe(z.number().default(defaultDynamicConfigs.bareNumber)),
  /**
   * Configuration for the background viewings import process.
   */
  viewingsImport: viewingsImportConfigSchema,

  /**
   * Configuration for the contact form handling.
   */
  contactForm: contactFormConfigSchema,

  /**
   * The oldest Passport version issued Okta tokens (OKTA-15).
   */
  oktaIssuancePassportMinVersion: stringValue.pipe(
    z.string().default(defaultDynamicConfigs.oktaIssuancePassportMinVersion),
  ),
};

export type DynamicConfigSchemas = typeof dynamicConfigSchemas;

/**
 * Sets up some default feature flags.
 *
 * This is where the flags are actually defined - all other types and the schema are derived from this.
 */
export const defaultFeatureFlags = {
  testFeatureFlagOne: false,
  testFeatureFlagTwo: true,
  testFeatureInAppDefaults: true,
  featureSignGymWaiver: true,
  featureBookAViewing: true,
  featureStripePayments: false,
  featureStripePaymentsQueue: false,
  // Okta migration (BCD-1739 epics). Evaluated server-side in companion-auth at mint time, per
  // client, so the clients never need to know which credential they were issued. Both stay
  // false until OKTA-14 / OKTA-18 flip them; each is independently reversible.
  featureOktaIssuancePortal: false,
  featureOktaIssuancePassport: false,
} as const;

export type DynamicConfigWithFeatureFlags = IDynamicConfig & { featureFlags: IFeatureFlags };
